import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, MapPin, Star, MessagesSquare, MessageCircle } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useLang } from "../context/LangContext";
import ProductCard from "../components/ProductCard";
import ChatDialog from "../components/ChatDialog";
import { useLiveStreams, sellerLiveStream } from "../lib/useLive";

export default function Storefront() {
  const { slug } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const liveStreams = useLiveStreams();

  useEffect(() => {
    setData(null);
    api(`/stores/${slug}`).then(setData).catch((e) => setError(e.message));
  }, [slug]);

  if (error)
    return (
      <div data-testid="store-error" className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="font-heading text-xl font-semibold text-slate-800">{t("empty_t")}</p>
        <Link to="/suppliers" className="mt-6 inline-block rounded-full bg-teal-700 px-5 py-2.5 text-sm font-semibold text-white">
          {t("nav_suppliers")}
        </Link>
      </div>
    );

  if (!data)
    return (
      <div data-testid="store-loading" className="max-w-7xl mx-auto px-4 py-16 animate-pulse">
        <div className="h-48 rounded-3xl bg-slate-100" />
        <div className="mt-6 h-8 w-1/3 rounded bg-slate-100" />
      </div>
    );

  const s = data.supplier;
  const liveStream = sellerLiveStream(liveStreams, { id: s.id || s.supplier_id || slug, name: s.name });

  return (
    <div data-testid="storefront-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
        <div className="relative h-44 sm:h-56 bg-teal-950">
          {s.image && <img src={mediaUrl(s.image)} alt={s.name} className="h-full w-full object-cover opacity-80" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        </div>
        <div className="relative px-5 sm:px-8 pb-6">
          <div className="-mt-10 flex h-20 w-20 items-center justify-center rounded-2xl border-4 border-white bg-teal-700 font-heading text-3xl font-extrabold text-white shadow-lg">
            {(s.name || "S")[0]}
          </div>
          <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-heading flex items-center gap-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                {s.name}
                {s.verified && <BadgeCheck data-testid="store-verified" className="h-6 w-6 text-teal-600" />}
                {liveStream && (
                  <Link
                    data-testid="store-live-badge"
                    to={`/live/${liveStream.id}`}
                    className="flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white shadow transition active:scale-95"
                  >
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Live
                  </Link>
                )}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-slate-600">
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-teal-600" /> {s.country}</span>
                <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {s.rating}</span>
                <span className="flex items-center gap-1"><MessagesSquare className="h-4 w-4 text-teal-600" /> {s.response_rate}% {t("response")}</span>
                {s.years > 0 && <span>{s.years} {t("yrs")}</span>}
              </div>
              {s.about && <p data-testid="store-about" className="mt-3 max-w-2xl text-sm text-slate-600 leading-relaxed">{s.about}</p>}
            </div>
            <button
              data-testid="store-contact-button"
              onClick={() => setChatOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-teal-700 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-teal-700/25 hover:bg-teal-800 transition active:scale-95"
            >
              <MessageCircle className="h-4 w-4" /> {t("contact_supplier")}
            </button>
          </div>
        </div>
      </div>

      <h2 className="font-heading mt-10 mb-6 text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
        {t("store_products")} ({(data.products || []).length})
      </h2>
      {(data.products || []).length === 0 ? (
        <div data-testid="store-products-empty" className="rounded-2xl border border-dashed border-slate-300 py-14 text-center text-sm text-slate-500">
          {t("no_products")}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {data.products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}

      <ChatDialog open={chatOpen} onOpenChange={setChatOpen} partnerName={s.name} />
    </div>
  );
}
